import { useEffect, useState, useCallback } from "react";
import { useApp } from "@/context/AppContext";
import { api, fileUrl } from "@/lib/api";
import { toast } from "sonner";
import { LayoutGrid, MessageSquareQuote, Inbox, Settings, LogOut, Plus, Pencil, Trash2, Star, Mail, Phone, Wrench, FileText } from "lucide-react";
import { Logo } from "@/components/site/Logo";
import ImageUpload from "@/components/admin/ImageUpload";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";

const LANGS = [["de", "Deutsch"], ["sl", "Slovensko"], ["hr", "Hrvatski"]];
const inp = "w-full bg-slate-50 border border-slate-200 px-3 py-2 focus:outline-none focus:border-accent2 transition-colors text-sm";

function LangField({ label, value = {}, onChange, textarea }) {
  const [active, setActive] = useState("de");
  const Comp = textarea ? "textarea" : "input";
  return (
    <div>
      <div className="flex items-center justify-between mb-1.5">
        <label className="text-xs uppercase tracking-widest text-slate-400">{label}</label>
        <div className="flex gap-1">
          {LANGS.map(([l]) => (
            <button key={l} type="button" onClick={() => setActive(l)}
              className={`px-2 py-0.5 text-[10px] font-bold uppercase ${active === l ? "bg-brand text-white" : "bg-slate-100 text-slate-500"}`}>{l}</button>
          ))}
        </div>
      </div>
      <Comp rows={textarea ? 4 : undefined} className={inp + (textarea ? " resize-none" : "")}
        value={value[active] || ""} onChange={(e) => onChange({ ...value, [active]: e.target.value })} />
    </div>
  );
}

const emptyProject = { title: {}, category: "fenster", location: "", scope: {}, description: {}, cover_image: null, before_image: null, after_image: null, order: 0, featured: false };
const emptyTest = { name: "", location: "", rating: 5, text: {}, order: 0, source: "website" };

function ProjectDialog({ project, onSaved, trigger }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(project || emptyProject);
  useEffect(() => { if (open) setF(project || emptyProject); }, [open, project]);
  const save = async () => {
    if (!f.title?.de) return toast.error("Titel (DE) erforderlich");
    try {
      if (f.id) await api.put(`/admin/projects/${f.id}`, f);
      else await api.post("/admin/projects", f);
      toast.success("Gespeichert"); setOpen(false); onSaved();
    } catch { toast.error("Fehler beim Speichern"); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto rounded-none">
        <DialogHeader><DialogTitle className="font-display">{f.id ? "Projekt bearbeiten" : "Neues Projekt"}</DialogTitle></DialogHeader>
        <div className="space-y-4 py-2">
          <LangField label="Titel" value={f.title} onChange={(v) => setF({ ...f, title: v })} />
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs uppercase tracking-widest text-slate-400">Kategorie</label>
              <select className={inp} value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })}>
                <option value="fenster">Fenster</option>
                <option value="sonnenschutz">Sonnenschutz</option>
              </select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-slate-400">Ort</label>
              <input className={inp} value={f.location} onChange={(e) => setF({ ...f, location: e.target.value })} />
            </div>
          </div>
          <LangField label="Umfang" value={f.scope} onChange={(v) => setF({ ...f, scope: v })} />
          <LangField label="Beschreibung" value={f.description} onChange={(v) => setF({ ...f, description: v })} textarea />
          <div className="grid grid-cols-3 gap-4">
            <ImageUpload label="Cover" value={f.cover_image} onChange={(v) => setF({ ...f, cover_image: v })} />
            <ImageUpload label="Vorher" value={f.before_image} onChange={(v) => setF({ ...f, before_image: v })} />
            <ImageUpload label="Nachher" value={f.after_image} onChange={(v) => setF({ ...f, after_image: v })} />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-slate-400">Reihenfolge</label>
            <input type="number" className={inp} value={f.order} onChange={(e) => setF({ ...f, order: parseInt(e.target.value) || 0 })} />
          </div>
        </div>
        <DialogFooter>
          <button onClick={save} data-testid="project-save" className="bg-brand text-white font-semibold px-6 py-2.5 hover:bg-accent2 transition-colors">Speichern</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function TestimonialDialog({ item, onSaved, trigger }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(item || emptyTest);
  useEffect(() => { if (open) setF(item || emptyTest); }, [open, item]);
  const save = async () => {
    if (!f.name) return toast.error("Name erforderlich");
    try {
      if (f.id) await api.put(`/admin/testimonials/${f.id}`, f);
      else await api.post("/admin/testimonials", f);
      toast.success("Gespeichert"); setOpen(false); onSaved();
    } catch { toast.error("Fehler beim Speichern"); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-w-lg rounded-none">
        <DialogHeader><DialogTitle className="font-display">{f.id ? "Kundenstimme bearbeiten" : "Neue Kundenstimme"}</DialogTitle></DialogHeader>
        <div className="space-y-4 py-2">
          <div className="grid grid-cols-2 gap-4">
            <div><label className="text-xs uppercase tracking-widest text-slate-400">Name</label><input className={inp} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></div>
            <div><label className="text-xs uppercase tracking-widest text-slate-400">Ort</label><input className={inp} value={f.location} onChange={(e) => setF({ ...f, location: e.target.value })} /></div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs uppercase tracking-widest text-slate-400">Bewertung</label>
              <select className={inp} value={f.rating} onChange={(e) => setF({ ...f, rating: parseInt(e.target.value) })}>
                {[5, 4, 3, 2, 1].map((n) => <option key={n} value={n}>{n} Sterne</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-slate-400">Quelle</label>
              <select data-testid="testimonial-source" className={inp} value={f.source || "website"} onChange={(e) => setF({ ...f, source: e.target.value })}>
                <option value="website">Website</option>
                <option value="google">Google-Rezension</option>
              </select>
            </div>
          </div>
          <LangField label="Text" value={f.text} onChange={(v) => setF({ ...f, text: v })} textarea />
        </div>
        <DialogFooter>
          <button onClick={save} data-testid="testimonial-save" className="bg-brand text-white font-semibold px-6 py-2.5 hover:bg-accent2 transition-colors">Speichern</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DeleteBtn({ onConfirm }) {
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <button className="p-2 text-slate-400 hover:text-red-600 transition-colors" data-testid="delete-btn"><Trash2 size={16} /></button>
      </AlertDialogTrigger>
      <AlertDialogContent className="rounded-none">
        <AlertDialogHeader><AlertDialogTitle>Wirklich löschen?</AlertDialogTitle><AlertDialogDescription>Dies kann nicht rückgängig gemacht werden.</AlertDialogDescription></AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel className="rounded-none">Abbrechen</AlertDialogCancel>
          <AlertDialogAction className="rounded-none bg-red-600 hover:bg-red-700" onClick={onConfirm}>Löschen</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export default function Admin() {
  const { logout, user, siteInfo } = useApp();
  const [tab, setTab] = useState("projects");
  const [projects, setProjects] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [info, setInfo] = useState({});
  const [svc, setSvc] = useState({});
  const [imp, setImp] = useState({});

  const load = useCallback(() => {
    api.get("/projects").then(({ data }) => setProjects(data));
    api.get("/admin/testimonials").then(({ data }) => setTestimonials(data));
    api.get("/admin/contacts").then(({ data }) => setContacts(data)).catch(() => {});
    api.get("/site-info").then(({ data }) => setInfo(data || {}));
    api.get("/services").then(({ data }) => setSvc(data || {}));
    api.get("/impressum").then(({ data }) => setImp(data || {}));
  }, []);
  useEffect(() => { load(); }, [load]);

  const delProject = async (id) => { await api.delete(`/admin/projects/${id}`); toast.success("Gelöscht"); load(); };
  const delTest = async (id) => { await api.delete(`/admin/testimonials/${id}`); toast.success("Gelöscht"); load(); };
  const approveTest = async (tm) => { await api.put(`/admin/testimonials/${tm.id}`, { ...tm, approved: true }); toast.success("Freigegeben"); load(); };
  const delContact = async (id) => { await api.delete(`/admin/contacts/${id}`); toast.success("Gelöscht"); load(); };
  const saveInfo = async () => { await api.put("/admin/site-info", info); toast.success("Einstellungen gespeichert"); };
  const saveSvc = async () => { await api.put("/admin/services", svc); toast.success("Leistungstexte gespeichert"); };
  const saveImp = async () => { await api.put("/admin/impressum", imp); toast.success("Impressum gespeichert"); };
  const setSvcField = (key, field, v) => setSvc({ ...svc, [key]: { ...(svc[key] || {}), [field]: v } });
  const setContact = (i, field, v) => {
    const list = [...(info.contacts || [])];
    list[i] = { ...list[i], [field]: v };
    setInfo({ ...info, contacts: list });
  };
  const addContact = () => setInfo({ ...info, contacts: [...(info.contacts || []), { name: "", role: "Geschäftsführer", phone: "" }] });
  const removeContact = (i) => setInfo({ ...info, contacts: (info.contacts || []).filter((_, k) => k !== i) });
  const toggleRead = async (c) => {
    await api.put(`/admin/contacts/${c.id}`, { read: !c.read });
    setContacts(contacts.map((x) => (x.id === c.id ? { ...x, read: !c.read } : x)));
  };
  const setInquiryNote = (id, note) => setContacts(contacts.map((x) => (x.id === id ? { ...x, note } : x)));
  const saveNote = async (c) => { await api.put(`/admin/contacts/${c.id}`, { note: c.note || "" }); toast.success("Notiz gespeichert"); };

  const tabs = [
    { key: "projects", label: "Projekte", icon: LayoutGrid, count: projects.length },
    { key: "testimonials", label: "Kundenstimmen", icon: MessageSquareQuote, count: testimonials.length },
    { key: "contacts", label: "Anfragen", icon: Inbox, count: contacts.length, badge: contacts.filter((c) => !c.read).length },
    { key: "services", label: "Leistungen", icon: Wrench },
    { key: "impressum", label: "Impressum", icon: FileText },
    { key: "settings", label: "Einstellungen", icon: Settings },
  ];

  return (
    <div className="min-h-screen bg-slate-50 flex">
      {/* Sidebar */}
      <aside className="w-64 bg-brand text-white flex-col hidden md:flex fixed inset-y-0">
        <div className="p-6 border-b border-white/10"><Logo dark /></div>
        <nav className="flex-1 p-4 space-y-1">
          {tabs.map((tb) => (
            <button key={tb.key} data-testid={`tab-${tb.key}`} onClick={() => setTab(tb.key)}
              className={`w-full flex items-center gap-3 px-4 py-3 text-sm font-medium transition-colors ${tab === tb.key ? "bg-accent2 text-white" : "text-slate-300 hover:bg-white/5"}`}>
              <tb.icon size={18} /> {tb.label}
              {tb.badge > 0 && <span className="ml-auto text-xs bg-accent2 text-white px-2 py-0.5 font-bold" data-testid="unread-badge">{tb.badge}</span>}
              {tb.count != null && <span className={`text-xs bg-white/10 px-2 py-0.5 ${tb.badge > 0 ? "" : "ml-auto"}`}>{tb.count}</span>}
            </button>
          ))}
        </nav>
        <div className="p-4 border-t border-white/10">
          <div className="text-xs text-slate-400 mb-2 px-2 truncate">{user?.email}</div>
          <button onClick={logout} data-testid="logout-btn" className="w-full flex items-center gap-3 px-4 py-3 text-sm text-slate-300 hover:bg-white/5 transition-colors">
            <LogOut size={18} /> Abmelden
          </button>
        </div>
      </aside>

      {/* Mobile top tabs */}
      <div className="md:hidden fixed top-0 inset-x-0 z-40 bg-brand text-white flex overflow-x-auto">
        {tabs.map((tb) => (
          <button key={tb.key} onClick={() => setTab(tb.key)} className={`flex-1 px-4 py-3 text-xs font-semibold whitespace-nowrap ${tab === tb.key ? "bg-accent2" : ""}`}>{tb.label}</button>
        ))}
      </div>

      <main className="flex-1 md:ml-64 p-6 md:p-10 pt-20 md:pt-10">
        {tab === "projects" && (
          <section>
            <div className="flex items-center justify-between mb-8">
              <h1 className="font-display font-bold text-3xl">Projekte</h1>
              <ProjectDialog onSaved={load} trigger={<button data-testid="add-project" className="inline-flex items-center gap-2 bg-brand text-white font-semibold px-5 py-2.5 hover:bg-accent2 transition-colors"><Plus size={18} /> Neu</button>} />
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {projects.map((p) => (
                <div key={p.id} className="bg-white border border-slate-200" data-testid={`admin-project-${p.id}`}>
                  <div className="aspect-[4/3] bg-slate-100 overflow-hidden">
                    {(p.cover_image || p.after_image) && <img src={fileUrl(p.cover_image || p.after_image)} alt="" className="w-full h-full object-cover" />}
                  </div>
                  <div className="p-4 flex items-center justify-between">
                    <div className="min-w-0">
                      <div className="text-xs uppercase tracking-widest text-accent2">{p.location || p.category}</div>
                      <div className="font-display font-bold truncate">{p.title?.de}</div>
                    </div>
                    <div className="flex shrink-0">
                      <ProjectDialog project={p} onSaved={load} trigger={<button className="p-2 text-slate-400 hover:text-brand"><Pencil size={16} /></button>} />
                      <DeleteBtn onConfirm={() => delProject(p.id)} />
                    </div>
                  </div>
                </div>
              ))}
              {projects.length === 0 && <p className="text-slate-500 col-span-full py-12 text-center border border-dashed">Noch keine Projekte.</p>}
            </div>
          </section>
        )}

        {tab === "testimonials" && (
          <section>
            <div className="flex items-center justify-between mb-8">
              <h1 className="font-display font-bold text-3xl">Kundenstimmen</h1>
              <TestimonialDialog onSaved={load} trigger={<button data-testid="add-testimonial" className="inline-flex items-center gap-2 bg-brand text-white font-semibold px-5 py-2.5 hover:bg-accent2 transition-colors"><Plus size={18} /> Neu</button>} />
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              {testimonials.map((tm) => (
                <div key={tm.id} className="bg-white border border-slate-200 p-5" data-testid={`admin-testimonial-${tm.id}`}>
                  <div className="flex items-start justify-between">
                    <div className="flex gap-1">{Array.from({ length: tm.rating }).map((_, k) => <Star key={k} size={14} className="fill-accent2 text-accent2" />)}</div>
                    <div className="flex">
                      <TestimonialDialog item={tm} onSaved={load} trigger={<button className="p-2 text-slate-400 hover:text-brand"><Pencil size={16} /></button>} />
                      <DeleteBtn onConfirm={() => delTest(tm.id)} />
                    </div>
                  </div>
                  <p className="mt-3 text-slate-700 text-sm line-clamp-3">"{tm.text?.de}"</p>
                  <div className="mt-3 font-semibold">{tm.name} <span className="text-slate-400 font-normal">· {tm.location}</span>{tm.source === "google" && <span className="ml-2 text-[10px] font-bold uppercase tracking-wider bg-blue-100 text-blue-700 px-1.5 py-0.5">Google</span>}</div>
                  {tm.approved === false && (
                    <div className="mt-3 flex items-center gap-2">
                      <span className="text-xs font-semibold uppercase tracking-wider bg-amber-100 text-amber-700 px-2 py-1">Wartet auf Freigabe</span>
                      <button data-testid={`approve-testimonial-${tm.id}`} onClick={() => approveTest(tm)}
                        className="text-xs font-semibold uppercase tracking-wider bg-brand text-white px-3 py-1 hover:bg-accent2 transition-colors">Freigeben</button>
                    </div>
                  )}
                </div>
              ))}
              {testimonials.length === 0 && <p className="text-slate-500 col-span-full py-12 text-center border border-dashed">Noch keine Kundenstimmen.</p>}
            </div>
          </section>
        )}

        {tab === "contacts" && (
          <section>
            <h1 className="font-display font-bold text-3xl mb-8">Anfragen</h1>
            <div className="space-y-3">
              {contacts.map((c) => (
                <div key={c.id} className={`bg-white border p-5 ${c.read ? "border-slate-200" : "border-l-4 border-l-accent2 border-slate-200"}`} data-testid={`contact-${c.id}`}>
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <div className="font-display font-bold text-lg">{c.name}</div>
                        {!c.read && <span className="text-[10px] font-bold uppercase tracking-wider bg-accent2 text-white px-2 py-0.5">Neu</span>}
                      </div>
                      <div className="flex flex-wrap gap-4 text-sm text-slate-500 mt-1">
                        <a href={`mailto:${c.email}`} className="flex items-center gap-1.5 hover:text-accent2"><Mail size={14} />{c.email}</a>
                        {c.phone && <a href={`tel:${c.phone}`} className="flex items-center gap-1.5 hover:text-accent2"><Phone size={14} />{c.phone}</a>}
                      </div>
                      <p className="mt-3 text-slate-700 whitespace-pre-line">{c.message}</p>
                      <div className="mt-2 text-xs text-slate-400">{new Date(c.created_at).toLocaleString("de-AT")}</div>
                      <div className="mt-4 border-t border-slate-100 pt-3">
                        <label className="text-[10px] uppercase tracking-widest text-slate-400">Notiz</label>
                        <div className="flex gap-2 mt-1">
                          <textarea data-testid={`inquiry-note-${c.id}`} rows={2} className={inp + " resize-none flex-1"}
                            placeholder="Interne Notiz zu dieser Anfrage …"
                            value={c.note || ""} onChange={(e) => setInquiryNote(c.id, e.target.value)} />
                          <button data-testid={`save-note-${c.id}`} onClick={() => saveNote(c)}
                            className="self-end text-xs font-semibold uppercase tracking-wider bg-brand text-white px-3 py-2 hover:bg-accent2 transition-colors">Speichern</button>
                        </div>
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-2 shrink-0">
                      <button data-testid={`toggle-read-${c.id}`} onClick={() => toggleRead(c)}
                        className={`text-xs font-semibold uppercase tracking-wider px-3 py-1.5 transition-colors ${c.read ? "bg-slate-100 text-slate-500 hover:bg-slate-200" : "bg-accent2 text-white hover:brightness-110"}`}>
                        {c.read ? "Als ungelesen markieren" : "Als gelesen markieren"}
                      </button>
                      <DeleteBtn onConfirm={() => delContact(c.id)} />
                    </div>
                  </div>
                </div>
              ))}
              {contacts.length === 0 && <p className="text-slate-500 py-12 text-center border border-dashed">Noch keine Anfragen.</p>}
            </div>
          </section>
        )}

        {tab === "services" && (
          <section className="max-w-2xl">
            <h1 className="font-display font-bold text-3xl mb-2">Leistungstexte</h1>
            <p className="text-slate-500 text-sm mb-8">Diese Texte erscheinen auf der Startseite und der Leistungen-Seite – in allen drei Sprachen.</p>
            <div className="space-y-6">
              {[["fenster", "Fenstermontage"], ["sonnenschutz", "Sonnenschutzmontage"]].map(([key, label]) => (
                <div key={key} className="bg-white border border-slate-200 p-6 md:p-8 space-y-5">
                  <h2 className="font-display font-bold text-xl">{label}</h2>
                  <LangField label="Name" value={svc[key]?.name || {}} onChange={(v) => setSvcField(key, "name", v)} />
                  <LangField label="Beschreibung" value={svc[key]?.desc || {}} onChange={(v) => setSvcField(key, "desc", v)} textarea />
                </div>
              ))}
              <button onClick={saveSvc} data-testid="save-services" className="bg-brand text-white font-semibold px-6 py-3 hover:bg-accent2 transition-colors">Speichern</button>
            </div>
          </section>
        )}

        {tab === "impressum" && (
          <section className="max-w-2xl">
            <h1 className="font-display font-bold text-3xl mb-2">Impressum / Firmendaten</h1>
            <p className="text-slate-500 text-sm mb-8">Diese Daten werden auf der Impressum-Seite angezeigt.</p>
            <div className="bg-white border border-slate-200 p-6 md:p-8 space-y-5">
              {[["company", "Firmenname (vollständig)"], ["owner", "Inhaber / Geschäftsführer"], ["email", "E-Mail"], ["phone", "Telefon (optional)"], ["register", "Registrierungsnummer / Matična številka"], ["tax", "Steuernummer / Davčna številka"], ["iban", "IBAN / Firmenkonto"]].map(([k, l]) => (
                <div key={k}>
                  <label className="text-xs uppercase tracking-widest text-slate-400">{l}</label>
                  <input data-testid={`imp-${k}`} className={inp} value={imp[k] || ""} onChange={(e) => setImp({ ...imp, [k]: e.target.value })} />
                </div>
              ))}
              <LangField label="Anschrift (mit Land)" value={typeof imp.address === "object" ? imp.address : {}} onChange={(v) => setImp({ ...imp, address: v })} textarea />
              <LangField label="Registereintrag (z. B. AJPES)" value={typeof imp.court === "object" ? imp.court : {}} onChange={(v) => setImp({ ...imp, court: v })} />
              <LangField label="Zusätzlicher Text (optional)" value={typeof imp.additional === "object" ? imp.additional : {}} onChange={(v) => setImp({ ...imp, additional: v })} textarea />
              <button onClick={saveImp} data-testid="save-impressum" className="bg-brand text-white font-semibold px-6 py-3 hover:bg-accent2 transition-colors">Speichern</button>
            </div>
          </section>
        )}

        {tab === "settings" && (
          <section className="max-w-2xl">
            <h1 className="font-display font-bold text-3xl mb-8">Einstellungen</h1>
            <div className="bg-white border border-slate-200 p-6 md:p-8 space-y-5">
              {[["company", "Firmenname (Anzeige)"], ["email", "E-Mail"], ["maps_query", "Google Maps Suchbegriff"], ["google_url", "Google-Profil-Link (Bewertungen)"], ["google_rating", "Google-Gesamtwertung (z. B. 5,0)"]].map(([k, l]) => (
                <div key={k}>
                  <label className="text-xs uppercase tracking-widest text-slate-400">{l}</label>
                  <input data-testid={`info-${k}`} className={inp} value={info[k] || ""} onChange={(e) => setInfo({ ...info, [k]: e.target.value })} />
                </div>
              ))}
              <LangField label="Adresse (mit Land)" value={typeof info.address === "object" ? info.address : {}} onChange={(v) => setInfo({ ...info, address: v })} />
              <LangField label="Öffnungszeiten" value={typeof info.hours === "object" ? info.hours : {}} onChange={(v) => setInfo({ ...info, hours: v })} textarea />
              <div>
                <div className="flex items-center justify-between mb-2">
                  <div className="text-xs uppercase tracking-widest text-slate-400">Ansprechpartner (Anruf & WhatsApp)</div>
                  <button data-testid="add-contact" onClick={addContact} className="text-xs font-semibold uppercase tracking-wider bg-slate-100 text-slate-600 px-3 py-1.5 hover:bg-slate-200 transition-colors">+ Hinzufügen</button>
                </div>
                <div className="space-y-3">
                  {(info.contacts || []).map((c, i) => (
                    <div key={i} className="grid grid-cols-[1fr_1fr_auto] gap-3 items-end border border-slate-100 p-3">
                      <div>
                        <label className="text-[10px] uppercase tracking-widest text-slate-400">Name</label>
                        <input data-testid={`contact-name-${i}`} className={inp} value={c.name || ""} onChange={(e) => setContact(i, "name", e.target.value)} />
                      </div>
                      <div>
                        <label className="text-[10px] uppercase tracking-widest text-slate-400">Telefon (+43 / +386 …)</label>
                        <input data-testid={`contact-phone-${i}`} className={inp} value={c.phone || ""} onChange={(e) => setContact(i, "phone", e.target.value)} />
                      </div>
                      <button data-testid={`remove-contact-${i}`} onClick={() => removeContact(i)} className="p-2 text-slate-400 hover:text-red-500 transition-colors"><Trash2 size={16} /></button>
                    </div>
                  ))}
                </div>
              </div>
              <button onClick={saveInfo} data-testid="save-settings" className="bg-brand text-white font-semibold px-6 py-3 hover:bg-accent2 transition-colors">Speichern</button>
            </div>
          </section>
        )}
      </main>
    </div>
  );
}
